/**
 * Sentry Integration for Frontend
 * Provides error tracking and performance monitoring
 */

import * as Sentry from '@sentry/react'
import { readReplayConsent } from './sentry-consent'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN_FRONTEND
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development'

const REPLAY_BODY_ALLOW_URLS = [/\/api\/feedback\/report(?:\/|$)/]
const REPLAY_BODY_DENY_URLS = [/\/auth\//i, /\/billing\//i]

let rememberedErrorEventId: string | null = null
let activeReplay: { stop: () => void } | null = null

export type FeedbackCorrelation = {
  feedbackEventId: string | null
  associatedErrorEventId: string | null
  sentryLastEventId: string | null
}

/**
 * Initialize Sentry for the React application
 * Call this before rendering the app
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    console.warn('[Sentry] VITE_SENTRY_DSN_FRONTEND not set - Sentry disabled')
    return
  }

  try {
    const hasReplayConsent = readReplayConsent()
    const replay = hasReplayConsent
      ? Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          networkCaptureBodies: true,
          networkDetailAllowUrls: REPLAY_BODY_ALLOW_URLS,
          networkDetailDenyUrls: REPLAY_BODY_DENY_URLS,
        })
      : null

    activeReplay = replay
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: `sensei-frontend@1.0.0`,

      // Performance monitoring
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

      // Replay is initialized only when the persistent consent is open.
      replaysSessionSampleRate: hasReplayConsent ? 0.1 : 0,
      replaysOnErrorSampleRate: hasReplayConsent ? 1.0 : 0,
      integrations: replay ? [replay] : [],

      // Don't send PII
      sendDefaultPii: false,

      // Filter out common non-critical errors
      beforeSend(event, hint) {
        const error = hint.originalException

        // Ignore network errors (user's connection issue)
        if (error instanceof TypeError && error.message.includes('fetch')) {
          return null
        }

        // Ignore cancelled requests
        if (error instanceof Error && error.name === 'AbortError') {
          return null
        }

        // Ignore ResizeObserver errors (browser quirk)
        if (
          error instanceof Error &&
          error.message.includes('ResizeObserver')
        ) {
          return null
        }

        return event
      },

      // Ignore specific URLs
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Firefox extensions
        /^moz-extension:\/\//i,
      ],
    })

    console.info(`[Sentry] Initialized successfully (env: ${ENVIRONMENT})`)
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error)
  }
}

/**
 * Set user context for Sentry events
 */
export function setSentryUser(user: {
  id?: string
  email?: string
  username?: string
}): void {
  try {
    Sentry.setUser(user)
  } catch {
    // Silently fail if Sentry not initialized
  }
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser(): void {
  try {
    Sentry.setUser(null)
  } catch {
    // Silently fail if Sentry not initialized
  }
}

/**
 * Manually capture an exception
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  captureExceptionAndRemember(error, context)
}

/**
 * Capture an exception and remember the exact SDK event ID for later feedback.
 */
export function captureExceptionAndRemember(
  error: Error,
  context?: Record<string, unknown>,
): string | null {
  try {
    const eventId = Sentry.captureException(error, { extra: context })
    rememberedErrorEventId = eventId ?? null
    return rememberedErrorEventId
  } catch {
    return null
  }
}

/**
 * Best-effort feedback capture with deliberately separate feedback/error IDs.
 */
export function captureFeedbackForSupport({
  message,
}: {
  message: string
}): FeedbackCorrelation {
  let sentryLastEventId: string | null = null
  try {
    sentryLastEventId = Sentry.lastEventId() ?? null
  } catch {
    // The observed last event is optional metadata only.
  }

  const associatedErrorEventId = rememberedErrorEventId ?? sentryLastEventId
  let feedbackEventId: string | null = null
  try {
    feedbackEventId = Sentry.captureFeedback({
      message,
      associatedEventId: associatedErrorEventId ?? undefined,
    }) ?? null
  } catch {
    // Feedback must never block the user's independent support submission.
  }

  return { feedbackEventId, associatedErrorEventId, sentryLastEventId }
}

/**
 * Stops an already-created Replay recorder after a user withdraws consent.
 */
export function stopReplayForWithdrawnConsent(): void {
  try {
    activeReplay?.stop()
  } catch {
    // A stopped/unavailable recorder must not affect the rest of the app.
  } finally {
    activeReplay = null
  }
}

/**
 * Manually capture a message
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  try {
    Sentry.captureMessage(message, level)
  } catch {
    // Silently fail if Sentry not initialized
  }
}

// Re-export Sentry ErrorBoundary for use in components
export const SentryErrorBoundary = Sentry.ErrorBoundary

/**
 * React 19+ Error Handlers for createRoot
 * These capture errors that ErrorBoundary might miss
 */
export const reactErrorHandler = Sentry.reactErrorHandler

/**
 * Get error handlers for React 19+ createRoot options
 * Usage: createRoot(el, getSentryErrorHandlers()).render(<App />)
 */
export function getSentryErrorHandlers() {
  return {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.error('Uncaught error:', error, errorInfo)
    }),
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Caught error:', error, errorInfo)
    }),
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Recoverable error:', error, errorInfo)
    }),
  }
}
